import React from 'react'
import { AccordionCard } from 'components/AccordionCard'
import { InfoTable } from 'components/InfoTable'
import { useCompanyDetailContextValue } from '../context/CompanyDetailProvider'
import {
  BriefcaseBusiness,
  Building2,
  Calendar,
  Clock,
  EllipsisVertical,
  Group,
  History,
  Pencil,
  ShieldCheck,
  SquareUser,
} from 'lucide-react'
import {
  Button,
  DropdownMenu,
  Flex,
  Link,
  Text,
  useAppTheme,
} from '@workwhile/ui'
import { useCompanyDetailWorkflowsContextValue } from '../context/CompanyDetailWorkflowProvider'
import { formatDistanceToNowStrict } from 'date-fns'
import { getLocalTimezone } from 'lib/time'
import { formatInTimeZone } from 'date-fns-tz'

export const GeneralDetails = () => {
  const { space } = useAppTheme()
  const {
    state: {
      type,
      requiresEverify,
      companyGroup,
      createdAt,
      salesOwner,
      accountOwner,
      dayforce,
      terminalTierThresholdHours,
      terminalTierThresholdDirection,
      pastShiftCreationAllowed,
    },
  } = useCompanyDetailContextValue()

  const timezone = getLocalTimezone()

  const createdOnDay = formatInTimeZone(createdAt, timezone, 'MMM d, yyyy')

  const createdOnDayDistance = formatDistanceToNowStrict(createdAt, {
    addSuffix: true,
  })

  const { editGeneralDetailsFlow } = useCompanyDetailWorkflowsContextValue()

  return (
    <AccordionCard
      title={
        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          width="100%"
        >
          <Text fontSize={2} color="lightText">
            General Details
          </Text>

          <DropdownMenu
            trigger={
              <Button
                variant="secondary"
                kind="small"
                style={{ paddingLeft: space[1], paddingRight: space[1] }}
              >
                <EllipsisVertical size={14} />
              </Button>
            }
          >
            <DropdownMenu.Item onClick={editGeneralDetailsFlow.open}>
              <Flex as="span" alignItems="center" style={{ gap: 8 }}>
                <Pencil size={14} />
                Edit
              </Flex>
            </DropdownMenu.Item>
          </DropdownMenu>
        </Flex>
      }
      // Intentionally disabled. This should work, unsure why it's throwing an error
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      maxWidth="50%"
    >
      <InfoTable>
        <InfoTable.Row
          icon={<Calendar size={14} />}
          label="Created at"
          value={`${createdOnDay} (${createdOnDayDistance})`}
        />
        <InfoTable.Row
          icon={<BriefcaseBusiness size={14} />}
          label="Type"
          value={type}
        />
        <InfoTable.Row
          icon={<ShieldCheck size={14} />}
          label="Requires E-Verify"
          value={requiresEverify ? 'Yes' : 'No'}
        />
        <InfoTable.Row
          icon={<SquareUser size={14} />}
          label="Account Owner"
          value={
            <Flex flexDirection="column" gap={1}>
              {accountOwner ? (
                <Text>
                  {accountOwner.name}{' '}
                  <Link to={`/users/${accountOwner.id}`} target="_blank">
                    ({accountOwner.id})
                  </Link>
                </Text>
              ) : (
                'Not set'
              )}
            </Flex>
          }
        />
        <InfoTable.Row
          icon={<SquareUser size={14} />}
          label="Sales Owner"
          value={
            <Flex flexDirection="column" gap={1}>
              {salesOwner ? (
                <Text>
                  {salesOwner.name}{' '}
                  <Link to={`/users/${salesOwner.id}`} target="_blank">
                    ({salesOwner.id})
                  </Link>
                </Text>
              ) : (
                'Not set'
              )}
            </Flex>
          }
        />
        <InfoTable.Row
          icon={<Group size={14} />}
          label="Company Group"
          value={
            companyGroup ? (
              <Flex flexDirection="column" gap={1}>
                <Text>
                  {companyGroup.name}{' '}
                  <Link to={`/company-groups/${companyGroup.id}`}>
                    ({companyGroup.id})
                  </Link>
                </Text>
              </Flex>
            ) : (
              'None'
            )
          }
        />
        <InfoTable.Row
          icon={<Clock size={14} />}
          label="Reserve tier guaranteed by"
          value={`${terminalTierThresholdHours}h ${terminalTierThresholdDirection === 'after_shift_create' ? 'after shifts are created' : 'before shifts start'}`}
        />
        <InfoTable.Row
          icon={<History size={14} />}
          label="Allows past shift creation"
          value={pastShiftCreationAllowed ? 'Yes' : 'No'}
        />
        {dayforce ? (
          <InfoTable.Row
            icon={<Building2 size={14} />}
            label="VMS Source"
            value="Dayforce"
          />
        ) : null}
      </InfoTable>
    </AccordionCard>
  )
}
